import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface Column<T> {
  id: Extract<keyof T, string> | 'actions';
  label: string;
  minWidth?: number;
  width?: string;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  /** No modo cards, usa esta coluna como título do card (padrão: a primeira coluna). */
  mobileTitle?: boolean;
  /** No modo cards, não exibe esta coluna (ex.: avatar, que não agrega em lista). */
  mobileHidden?: boolean;
}

interface DynamicTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof T, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  searchPlaceholder?: string;
  noDataText?: string;
  rowId?: Extract<keyof T, string>;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => React.ReactNode;
  onRefresh?: () => void;
  showSearch?: boolean;
  stickyHeader?: boolean;
  dense?: boolean;
  /**
   * Quando o espaço disponível ao componente é estreito (celular, tablet ou
   * desktop com menu lateral aberto) troca a tabela por uma lista de cards,
   * um por registro, com os campos empilhados. Desligado por padrão para não
   * alterar as telas que já usam o componente.
   */
  mobileCards?: boolean;
  /** Largura (px) do próprio componente abaixo da qual os cards são usados. */
  cardsBelow?: number;
}

export function DynamicTable<T>({
  columns,
  data,
  loading = false,
  error = null,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  searchPlaceholder = 'Pesquisar...',
  noDataText = 'Nenhum registro encontrado',
  rowId = 'id' as Extract<keyof T, string>,
  onRowClick,
  actions,
  onRefresh,
  showSearch = true,
  stickyHeader = true,
  dense = false,
  mobileCards = false,
  cardsBelow = 960,
}: DynamicTableProps<T>) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  // Mede o espaço real do componente (e não a janela): com o menu lateral aberto
  // o conteúdo é bem mais estreito que a tela.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!mobileCards || !el) return;
    setContainerWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [mobileCards]);
  const showCards = mobileCards && containerWidth !== undefined && containerWidth < cardsBelow;
  const [orderBy, setOrderBy] = useState<Extract<keyof T, string>>();
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      onSearch(searchTerm);
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm]);

  const handleRequestSort = (property: Extract<keyof T, string>) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    onSort(property, newOrder);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <Paper
      ref={containerRef}
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
      }}
      className="dark:bg-gray-800 dark:text-white"
    >
      {showSearch && (
        <Box p={2} display="flex" gap={1} alignItems="center">
          <TextField
            fullWidth
            variant="outlined"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="dark:bg-gray-200 dark:text-white"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {onRefresh && (
            <IconButton onClick={onRefresh} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          )}
        </Box>
      )}

      {showCards ? (
        <Box className="flex flex-col gap-3 px-3 pb-3">
          {loading ? (
            [...Array(Math.min(rowsPerPage, 5))].map((_, index) => (
              <Box key={index} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <Skeleton animation="wave" width="60%" />
                <Skeleton animation="wave" />
                <Skeleton animation="wave" width="80%" />
              </Box>
            ))
          ) : error ? (
            <Box py={3} textAlign="center">
              <Typography variant="body1" color="error">{error}</Typography>
            </Box>
          ) : data.length === 0 ? (
            <Box py={3} textAlign="center" color="text.secondary">
              <Typography variant="body1" color="textSecondary">{noDataText}</Typography>
            </Box>
          ) : (
            data.map((row) => {
              const dataColumns = columns.filter((column) => column.id !== 'actions' && !column.mobileHidden);
              const actionsColumn = columns.find((column) => column.id === 'actions');
              const titleColumn = dataColumns.find((column) => column.mobileTitle) ?? dataColumns[0];
              const detailColumns = dataColumns.filter((column) => column !== titleColumn);
              const renderValue = (column: Column<T>) => {
                const value = row[column.id as Extract<keyof T, string>];
                return column.format ? column.format(value, row) : String(value || '');
              };

              return (
                <Box
                  key={String(row[rowId])}
                  onClick={() => handleRowClick(row)}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white p-3 min-w-0"
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {titleColumn && (
                    <Box className="font-semibold mb-2 break-words min-w-0">{renderValue(titleColumn)}</Box>
                  )}
                  {detailColumns.length > 0 && (
                    <Box component="dl" className="m-0 flex flex-col gap-1.5">
                      {detailColumns.map((column) => (
                        <Box key={String(column.id)} className="flex items-start justify-between gap-3 text-sm">
                          <Box component="dt" className="text-gray-500 dark:text-gray-400 flex-shrink-0 max-w-[45%]">
                            {column.label}
                          </Box>
                          <Box component="dd" className="m-0 min-w-0 text-right break-words">
                            {renderValue(column)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {(actionsColumn || actions) && (
                    <Box className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap justify-end gap-2 [&>div]:flex-wrap [&>div]:justify-end [&>div]:gap-2 [&>div]:space-x-0">
                      {actionsColumn && renderValue(actionsColumn)}
                      {actions && actions(row)}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      ) : (
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader={stickyHeader} size={dense ? 'small' : 'medium'} className="dark:bg-gray-800 dark:text-white">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align || 'left'}
                  style={{
                    minWidth: column.minWidth,
                    width: column.width,
                    fontWeight: 600,
                  }}
                  className="bg-gray-200 dark:bg-gray-700 dark:text-white"
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ py: 1, px: 2 }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id as Extract<keyof T, string>)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={{ display: 'none' }}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions && (
                <TableCell
                  align="right"
                  style={{
                    fontWeight: 600,
                  }}
                  className="bg-gray-200 dark:bg-gray-700 dark:text-white"
                  sx={{ py: 1, px: 2 }}
                >
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <>
                {[...Array(rowsPerPage)].map((_, index) => (
                  <TableRow className="dark:bg-gray-800 dark:text-white" key={index}>
                    {columns.map((column) => (
                      <TableCell className="dark:bg-gray-800 dark:text-white" key={String(column.id)} sx={{ py: 1, px: 2 }}>
                        <Skeleton animation="wave" />
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell className="dark:bg-gray-800 dark:text-white" sx={{ py: 1, px: 2 }}>
                        <Skeleton animation="wave" />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </>
            ) : error ? (
              <TableRow>
                <TableCell className="dark:bg-gray-800 dark:text-white" colSpan={columns.length + (actions ? 1 : 0)} align="center">
                  <Box py={4} color="error.main">
                    <Typography variant="body1" color="error">
                      {error}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell className="dark:bg-gray-800 dark:text-white" colSpan={columns.length + (actions ? 1 : 0)} align="center">
                  <Box py={4} color="text.secondary">
                    <Typography variant="body1" color="textSecondary">
                      {noDataText}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  hover
                  key={String(row[rowId])}
                  onClick={() => handleRowClick(row)}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  className="dark:bg-gray-800 dark:text-white"
                >
                  {columns.map((column) => {
                    const value = row[column.id as Extract<keyof T, string>];
                    return (
                      <TableCell
                        key={String(column.id)}
                        align={column.align || 'left'}
                        sx={{ py: 1, px: 2 }}
                        className="dark:bg-gray-800 dark:text-white"
                      >
                        {column.format ? column.format(value, row) : String(value || '')}
                      </TableCell>
                    );
                  })}
                  {actions && (
                    <TableCell className="dark:bg-gray-800 dark:text-white" align="right" sx={{ py: 1, px: 2 }}>
                      {actions(row)}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={showCards ? 'Linhas:' : 'Linhas por página:'}
        className="dark:bg-gray-800 dark:text-white"
        sx={showCards ? {
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'center', px: 1 },
          '& .MuiTablePagination-spacer': { display: 'none' },
        } : undefined}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  );
}
